// HEAL-9: навигация в карточку маркера и обратно — сохранение состояния экрана-источника.
//
// Карточку маркера (/markers/:code) можно открыть из ДВУХ экранов: Главная (/) и
// Маркеры (/markers). Оба используют MobileFrame + scrollable <main> + аккордеоны и
// ПОЛНОСТЬЮ перемонтируются при возврате (wouter Switch), теряя scroll + раскрытые
// блоки + (на Маркерах) фильтр/поиск. Кэшируем ephemeral-состояние per-screen и
// восстанавливаем при remount. Сам scroll возвращает ScrollToTop в App (он владеет
// скроллом), здесь — хранилище + «откуда открыли».

import type { BiomarkerListFilter } from '@/api/types';

/** Сегмент вкладки «Маркеры» (Sprint 6-organs D1; дефолт «По категориям» — HEAL-24). */
export type MarkersSegment = 'organs' | 'categories';

export interface ListScreenState {
  /** scrollTop контейнера <main> на момент ухода в карточку. */
  scrollTop: number;
  /** code категории → раскрыта ли (override авто-expand). Отсутствие = дефолт. */
  openByCode: Record<string, boolean>;
  /** Только экран «Маркеры». */
  filter?: BiomarkerListFilter;
  search?: string;
  /** Только экран «Маркеры» — активный сегмент (органы/категории). */
  segment?: MarkersSegment;
}

const screens: Record<string, ListScreenState> = {};

export function saveListScreenState(key: string, state: ListScreenState): void {
  screens[key] = state;
}

export function readListScreenState(key: string): ListScreenState | undefined {
  return screens[key];
}

/** Путь экрана-источника → ключ кэша. null — экран не поддерживает restore. */
export function screenKeyForPath(path: string): string | null {
  if (path === '/') return 'home';
  if (path === '/markers') return 'markers';
  return null;
}

// Откуда открыли карточку маркера — чтобы «Назад» вернулся на экран-источник
// (Главная или Маркеры), а не жёстко на /markers. null = открыли НЕ из приложения
// (прямой заход по ссылке/refresh) → «Назад» падает на безопасный фолбэк /markers.
let markerOrigin: string | null = null;

export function setMarkerOrigin(path: string): void {
  markerOrigin = path;
}

/**
 * ЧИСТОЕ чтение (без мутации) — безопасно звать в render / useState-initializer, в т.ч.
 * под двойным рендером StrictMode. Возвращает, выставлен ли origin (= карточку открыли
 * из приложения). Сам сброс делает clearMarkerOrigin() в useEffect (там, где side-effect'у
 * и место), чтобы render оставался чистым.
 */
export function peekMarkerOrigin(): boolean {
  return markerOrigin !== null;
}

/**
 * Сброс origin (one-shot consume) — вызывать в useEffect, НЕ в render. Идемпотентно:
 * повторный вызов (двойной mount эффекта в StrictMode) — no-op. Так stale-origin не
 * переживает detail-визит: следующий прямой заход по ссылке увидит false → «Назад»
 * уйдёт на безопасный фолбэк /markers. Origin выставляется ТОЛЬКО при переходе в
 * карточку маркера (не /markers/extra).
 */
export function clearMarkerOrigin(): void {
  markerOrigin = null;
}

/**
 * Полная очистка nav-state — на logout и 401. Иначе module-scope кэш (поиск/фильтр/
 * раскрытые блоки + origin) пережил бы смену пользователя без reload → privacy-утечка
 * (user B увидел бы search/filter user A на /markers). Привязки к user id нет, поэтому
 * чистим явно при разлогине/401.
 */
export function clearMarkerNavState(): void {
  for (const key of Object.keys(screens)) {
    delete screens[key];
  }
  markerOrigin = null;
}
